import * as React from 'react';
import {useState, useRef, useEffect, useCallback, useMemo, memo} from 'react';
import {Map, Marker, Source, Layer} from 'react-map-gl';
import Pin from './Pin';
import {clusterLayer, currentLocationLabel, clusterCountLayer, unclusteredPointLayer, unclusteredPointTwinkleLayer, pointLayer, pointLayerLabel, currentLocationLayer, ab604FillLayer} from '../utils/layers';
import '../App.css';
import Auth from '../utils/auth'
import { lineWobble } from 'ldrs';
import STATE_CAPITALS from '../utils/STATE_CAPITALS';
// TODO: Move to .env file.
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

lineWobble.register();

const seededFromString = (value, salt = 0) => {
  const text = `${value}:${salt}`;
  let hash = 2166136261;

  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) / 4294967295;
};

const PostMap = (props) => {
  const mapRef = useRef();
  const bounds = mapRef.current
  ? mapRef.current
      .getMap()
      .getBounds()
      .toArray()
      .flat()
  : null;

  const map = mapRef.current?.getMap();
  const [currentLocationData, setCurrentLocationData] = useState(null);
  const [maxClusterSize, setMaxClusterSize] = useState(0); // Add this line
  const [pointsLoaded, setPointsLoaded] = useState(false); // New state for tracking data load
  const [boxStart, setBoxStart] = useState(null);
  const [boxCurrent, setBoxCurrent] = useState(null);
  const postPointData = useMemo(() => {
    const geojson = props.postGeoJSON;
    if (!geojson || !Array.isArray(geojson.features)) return geojson;

    return {
      ...geojson,
      features: geojson.features.map((feature, index) => {
        const properties = feature.properties || {};
        const key =
          properties._id ||
          properties.id ||
          feature.id ||
          `${feature.geometry?.type || 'feature'}-${index}`;

        const flickerPeriodMs = 1000 + Math.floor(seededFromString(key, 1) * 500);
        const flickerPhaseMs = Math.floor(seededFromString(key, 2) * flickerPeriodMs);
        const pulsePeriodMs = 1500 + Math.floor(seededFromString(key, 4) * 500);
        const pulsePhaseMs = Math.floor(seededFromString(key, 5) * pulsePeriodMs);

        return {
          ...feature,
          properties: {
            ...properties,
            flickerPeriodMs,
            flickerPhaseMs,
            pulsePeriodMs,
            pulsePhaseMs
          }
        };
      })
    };
  }, [props.postGeoJSON]);
  const updateMaxClusterSize = () => {
    const maxClusterSize = 25;
    if (!map) return;

    const clusters = map.querySourceFeatures('postPoints', {
      filter: ['has', 'point_count']
    });

    const maxCount = clusters.reduce(
      (max, cluster) => Math.max(max, cluster.properties.point_count),
      0
    );
    setMaxClusterSize(Math.max(maxClusterSize, maxCount));
    setPointsLoaded(true);
  };

  
  const pointData = useMemo(() => {
    const longitude = Number.isFinite(props.centerLongitude)
      ? props.centerLongitude
      : props.viewport?.longitude;
    const latitude = Number.isFinite(props.centerLatitude)
      ? props.centerLatitude
      : props.viewport?.latitude;
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;

    const iconColor = props.reportIconColor || '#462b65';

    return {
      type: 'Feature',
      properties: {
        iconColor
      },
      geometry: {
        type: 'Point',
        coordinates: [longitude, latitude]
      }
    };
  }, [
    props.centerLatitude,
    props.centerLongitude,
    props.reportIconColor,
    props.viewport?.latitude,
    props.viewport?.longitude
  ]);

  // useEffect(() => {
  //   updateMaxClusterSize();
  // }, [props.postGeoJSON]);

  useEffect(() => {
    setCurrentLocationData({
      type: 'Point',
      coordinates: [props.currentLongitude, props.currentLatitude  + 0.0005]
    })
  }, [props.currentLatitude, props.currentLongitude]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current.getMap();
  
    const handleClusterClick = (e) => {
      const clusterId = e.features[0].properties.cluster_id;
      const pointCount = e.features[0].properties.point_count;
      const clusterSource = mapRef.current.getSource('postPoints');
      clusterSource.getClusterLeaves(clusterId, pointCount, 0, (error, features) => {
        props.setSelectedMapPosts(features);
        props.setIsSelectedPaneOpen(true);
      });
    };
  
    const handleUnclusteredPointClick = (e) => {
      const feature = e.features.splice(0, 1);
      if (feature) {
        props.setSelectedMapPosts(feature);
        props.setIsSelectedPaneOpen(true);
      }
    };
  
    const handleProjectCivilianClick = (e) => {
      if (Auth.loggedIn()) {
        props.setIsEditMode(false);
        props.setIsPostPaneOpen(true);
      } else {
        props.setIsRegisterPaneOpen(true);
      }
    };
  
    // Register event listeners once when the map loads.
    map.on('click', 'clusters', handleClusterClick);
    map.on('click', 'unclustered-point', handleUnclusteredPointClick);
    map.on('click', 'projectCivilian', handleProjectCivilianClick);
  
    // Cleanup event listeners on unmount.
    return () => {
      map.off('click', 'clusters', handleClusterClick);
      map.off('click', 'unclustered-point', handleUnclusteredPointClick);
      map.off('click', 'projectCivilian', handleProjectCivilianClick);
    };
  }, [props]); // Include only stable dependencies.

  useEffect(() => {
    let animationFrameId = null;
    let lastTick = 0;
    const frameInterval = 1000 / 18;
    const twinkleLayerId = 'unclustered-point-twinkle';
    const outlineLayerId = 'unclustered-point';
    const reportLayerId = 'projectCivilian';
    const outlineBlack = 'rgba(0, 0, 0, 1)';
    const reportHalo = 'rgba(154, 154, 154, 1)';
    const twoPi = 6.283185307179586;
    const twinkleRadiusScale = 0.25;
    const maxUnclusteredRadiusPx = 12;
    const visibleUnclusteredPauseThreshold = 50;
    const visibilityCheckIntervalMs = 250;
    let isUnclusteredAnimationPaused = false;
    let lastVisibilityCheck = 0;

    const getPrimaryPhase = (now) => [
      '*',
      twoPi,
      [
        '/',
        [
          '+',
          now,
          ['coalesce', ['to-number', ['get', 'pulsePhaseMs']], 0]
        ],
        ['coalesce', ['to-number', ['get', 'pulsePeriodMs']], 1750]
      ]
    ];

    const getSecondaryPhase = (now) => [
      '*',
      twoPi,
      [
        '/',
        [
          '+',
          now,
          ['*', ['coalesce', ['to-number', ['get', 'pulsePhaseMs']], 0], 1.63]
        ],
        ['coalesce', ['to-number', ['get', 'pulsePeriodMs']], 1750]
      ]
    ];

    const getTwinkleExpression = (now) => [
      '+',
      0.07,
      [
        '*',
        0.1,
        [
          '+',
          1,
          ['sin', getPrimaryPhase(now)]
        ]
      ],
      [
        '*',
        0.05,
        [
          '+',
          1,
          ['sin', getSecondaryPhase(now)]
        ]
      ]
    ];

    const steppedByHalfZoom = (
      minZoom,
      maxZoom,
      minValue,
      maxValue,
      createStopValue = (value) => value
    ) => {
      const expression = ['step', ['zoom'], createStopValue(minValue, minZoom)];
      const range = maxZoom - minZoom;
      if (!Number.isFinite(range) || range <= 0) return expression;

      const stepSize = 0.5;
      for (let zoom = minZoom; zoom <= maxZoom + 1e-9; zoom += stepSize) {
        const z = Math.min(maxZoom, Number(zoom.toFixed(6)));
        const t = (z - minZoom) / range;
        const value = Number((minValue + (maxValue - minValue) * t).toFixed(3));
        const stopZoom = Number(z.toFixed(1));
        expression.push(stopZoom, createStopValue(value, stopZoom));
      }

      const lastStop = expression[expression.length - 2];
      if (!Number.isFinite(lastStop) || Math.abs(lastStop - maxZoom) > 1e-6) {
        expression.push(maxZoom, createStopValue(maxValue, maxZoom));
      }

      return expression;
    };

    const getTwinkleRadiusExpression = (now) => {
      const twinklePulse = [
        '*',
        0.7,
        [
          '+',
          1,
          ['sin', getPrimaryPhase(now)]
        ]
      ];

      return steppedByHalfZoom(5, 13, 9.3, 16, (baseRadius) => [
        '*',
        twinkleRadiusScale,
        ['+', baseRadius, twinklePulse]
      ]);
    };

    const getOutlinePrimaryPhase = (now) => [
      '*',
      twoPi,
      [
        '/',
        [
          '+',
          now,
          ['coalesce', ['to-number', ['get', 'flickerPhaseMs']], 0]
        ],
        ['coalesce', ['to-number', ['get', 'flickerPeriodMs']], 1250]
      ]
    ];

    const getOutlineSecondaryPhase = (now) => [
      '*',
      twoPi,
      [
        '/',
        [
          '+',
          ['*', now, 1.08],
          ['*', ['coalesce', ['to-number', ['get', 'flickerPhaseMs']], 0], 1.73]
        ],
        ['coalesce', ['to-number', ['get', 'flickerPeriodMs']], 1250]
      ]
    ];

    const getOutlineBreath = (now) => [
      'min',
      1,
      [
        'max',
        0,
        [
          '+',
          0.5,
          ['*', 0.34, ['sin', getOutlinePrimaryPhase(now)]],
          ['*', 0.16, ['sin', getOutlineSecondaryPhase(now)]]
        ]
      ]
    ];

    const getOutlineStrokeWidthExpression = (now) => {
      const breath = getOutlineBreath(now);
      return steppedByHalfZoom(5, 13, 1.45, 2.45, (baseWidth) => [
        '+',
        baseWidth,
        ['*', 0.5, breath]
      ]);
    };

    const getOutlineStrokeOpacityExpression = (now) => [
      '+',
      0.1,
      ['*', 0.8, getOutlineBreath(now)]
    ];

    const getOutlineRadiusExpression = (now) => {
      const breath = getOutlineBreath(now);
      return steppedByHalfZoom(5, 13, 6.2, 11, (baseRadius) => [
        'min',
        maxUnclusteredRadiusPx,
        [
          '+',
          baseRadius,
          ['*', 0.35, breath]
        ]
      ]);
    };

    const getReportPrimaryPhase = (now) => [
      '*',
      twoPi,
      ['/', now, 2200]
    ];

    const getReportSecondaryPhase = (now) => [
      '*',
      twoPi,
      [
        '/',
        ['+', ['*', now, 1.07], 430],
        3000
      ]
    ];

    const getReportDepth = (now) => [
      'min',
      1,
      [
        'max',
        0,
        [
          '+',
          0.5,
          ['*', 0.3, ['sin', getReportPrimaryPhase(now)]],
          ['*', 0.2, ['sin', getReportSecondaryPhase(now)]]
        ]
      ]
    ];

    const getReportRadiusExpression = (now) => [
      '+',
      9.8,
      ['*', 0.7, getReportDepth(now)]
    ];

    const getReportStrokeWidthExpression = (now) => [
      '+',
      18.2,
      ['*', 4.2, getReportDepth(now)]
    ];

    const getReportStrokeOpacityExpression = (now) => [
      '+',
      0.06,
      ['*', 0.08, getReportDepth(now)]
    ];

    const updateUnclusteredPauseState = (map, now) => {
      if (now - lastVisibilityCheck < visibilityCheckIntervalMs) {
        return isUnclusteredAnimationPaused;
      }

      lastVisibilityCheck = now;

      if (!map || !map.getLayer(outlineLayerId)) {
        isUnclusteredAnimationPaused = false;
        return isUnclusteredAnimationPaused;
      }

      try {
        const visibleUnclusteredCount = map.queryRenderedFeatures({
          layers: [outlineLayerId]
        }).length;
        isUnclusteredAnimationPaused =
          visibleUnclusteredCount > visibleUnclusteredPauseThreshold;
      } catch (error) {
        isUnclusteredAnimationPaused = false;
      }

      return isUnclusteredAnimationPaused;
    };

    const animate = (now) => {
      const map = mapRef.current?.getMap();
      if (map && now - lastTick >= frameInterval) {
        lastTick = now;
        const pauseUnclusteredAnimation = updateUnclusteredPauseState(map, now);

        if (!pauseUnclusteredAnimation && map.getLayer(twinkleLayerId)) {
          map.setPaintProperty(twinkleLayerId, 'circle-opacity', getTwinkleExpression(now));
          map.setPaintProperty(twinkleLayerId, 'circle-radius', getTwinkleRadiusExpression(now));
        }

        if (!pauseUnclusteredAnimation && map.getLayer(outlineLayerId)) {
          map.setPaintProperty(outlineLayerId, 'circle-radius', getOutlineRadiusExpression(now));
          map.setPaintProperty(outlineLayerId, 'circle-stroke-color', outlineBlack);
          map.setPaintProperty(outlineLayerId, 'circle-stroke-width', getOutlineStrokeWidthExpression(now));
          map.setPaintProperty(outlineLayerId, 'circle-stroke-opacity', getOutlineStrokeOpacityExpression(now));
        }

        if (map.getLayer(reportLayerId)) {
          map.setPaintProperty(reportLayerId, 'circle-radius', getReportRadiusExpression(now));
          map.setPaintProperty(reportLayerId, 'circle-stroke-color', reportHalo);
          map.setPaintProperty(reportLayerId, 'circle-stroke-width', getReportStrokeWidthExpression(now));
          map.setPaintProperty(reportLayerId, 'circle-stroke-opacity', getReportStrokeOpacityExpression(now));
        }
      }

      animationFrameId = window.requestAnimationFrame(animate);
    };

    animationFrameId = window.requestAnimationFrame(animate);
    return () => {
      const map = mapRef.current?.getMap();
      if (map && map.getLayer(outlineLayerId)) {
        map.setPaintProperty(outlineLayerId, 'circle-radius', steppedByHalfZoom(5, 13, 6.2, 11));
        map.setPaintProperty(outlineLayerId, 'circle-stroke-color', outlineBlack);
        map.setPaintProperty(outlineLayerId, 'circle-stroke-width', steppedByHalfZoom(5, 13, 1.6, 2.6));
        map.setPaintProperty(outlineLayerId, 'circle-stroke-opacity', 0.9);
      }
      if (map && map.getLayer(reportLayerId)) {
        map.setPaintProperty(reportLayerId, 'circle-radius', 10);
        map.setPaintProperty(reportLayerId, 'circle-stroke-color', reportHalo);
        map.setPaintProperty(reportLayerId, 'circle-stroke-width', 20);
        map.setPaintProperty(reportLayerId, 'circle-stroke-opacity', 0.09);
      }
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const toBounds = useCallback((start, end) => {
    if (!start || !end) return null;
    const minLng = Math.min(start.lng, end.lng);
    const maxLng = Math.max(start.lng, end.lng);
    const minLat = Math.min(start.lat, end.lat);
    const maxLat = Math.max(start.lat, end.lat);
    return [minLng, minLat, maxLng, maxLat];
  }, []);

  const getLngLatFromTouchEvent = useCallback((event) => {
    if (event?.lngLat) return event.lngLat;
    const map = mapRef.current?.getMap();
    if (!map) return null;
    const touch =
      event?.originalEvent?.touches?.[0] ||
      event?.originalEvent?.changedTouches?.[0];
    if (!touch) return null;
    const rect = map.getContainer().getBoundingClientRect();
    const point = [touch.clientX - rect.left, touch.clientY - rect.top];
    return map.unproject(point);
  }, []);

  const boxBounds = (() => {
    if (boxStart && boxCurrent) return toBounds(boxStart, boxCurrent);
    if (Array.isArray(props.focusBox) && props.focusBox.length === 4) return props.focusBox;
    return null;
  })();

  const boxGeoJSON = boxBounds
    ? {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Polygon',
              coordinates: [
                [
                  [boxBounds[0], boxBounds[1]],
                  [boxBounds[2], boxBounds[1]],
                  [boxBounds[2], boxBounds[3]],
                  [boxBounds[0], boxBounds[3]],
                  [boxBounds[0], boxBounds[1]],
                ],
              ],
            },
          },
        ],
      }
    : null;

  const geoJSONFilterDisplayData = useMemo(() => {
    const input = props.geoJSONFilterGeometry;
    return input && typeof input === 'object' ? input : null;
  }, [props.geoJSONFilterGeometry]);

  const geoJSONFilterBounds = useMemo(() => {
    if (!geoJSONFilterDisplayData || typeof geoJSONFilterDisplayData !== 'object') return null;

    let minLat = Infinity;
    let maxLat = -Infinity;
    const longitudes = [];

    const normalizeLongitude = (lng) => {
      const value = Number(lng);
      if (!Number.isFinite(value)) return null;
      const normalized = ((value % 360) + 360) % 360;
      return normalized;
    };

    const getWrappedLongitudeBounds = (values) => {
      if (!values.length) return null;
      if (values.length === 1) {
        const lone = values[0];
        return [lone, lone];
      }

      const normalized = values
        .map(normalizeLongitude)
        .filter((value) => value != null)
        .sort((a, b) => a - b);

      if (!normalized.length) return null;

      let maxGap = -Infinity;
      let maxGapIndex = 0;
      const count = normalized.length;

      for (let i = 0; i < count; i += 1) {
        const current = normalized[i];
        const next = i === count - 1 ? normalized[0] + 360 : normalized[i + 1];
        const gap = next - current;
        if (gap > maxGap) {
          maxGap = gap;
          maxGapIndex = i;
        }
      }

      const start = maxGapIndex === count - 1 ? normalized[0] : normalized[maxGapIndex + 1];
      const end = start + (360 - maxGap);
      const center = (start + end) / 2;
      const shift = center > 180 ? -360 : 0;

      return [start + shift, end + shift];
    };

    const collectCoordinates = (coordinates) => {
      if (!Array.isArray(coordinates)) return;
      if (
        coordinates.length >= 2 &&
        typeof coordinates[0] === 'number' &&
        typeof coordinates[1] === 'number'
      ) {
        const lon = Number(coordinates[0]);
        const lat = Number(coordinates[1]);
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
        longitudes.push(lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        return;
      }

      coordinates.forEach((entry) => collectCoordinates(entry));
    };

    const collectGeometryBounds = (geometry) => {
      if (!geometry || typeof geometry !== 'object') return;
      if (geometry.type === 'GeometryCollection') {
        const geometries = Array.isArray(geometry.geometries) ? geometry.geometries : [];
        geometries.forEach((entry) => collectGeometryBounds(entry));
        return;
      }
      if (Array.isArray(geometry.coordinates)) {
        collectCoordinates(geometry.coordinates);
      }
    };

    const collectGeoJSONBounds = (geojson) => {
      if (!geojson || typeof geojson !== 'object') return;
      if (geojson.type === 'FeatureCollection') {
        const features = Array.isArray(geojson.features) ? geojson.features : [];
        features.forEach((feature) => collectGeometryBounds(feature?.geometry));
        return;
      }
      if (geojson.type === 'Feature') {
        collectGeometryBounds(geojson.geometry);
        return;
      }
      collectGeometryBounds(geojson);
    };

    collectGeoJSONBounds(geoJSONFilterDisplayData);

    const lngBounds = getWrappedLongitudeBounds(longitudes);
    if (!lngBounds || !Number.isFinite(minLat) || !Number.isFinite(maxLat)) return null;
    return [lngBounds[0], minLat, lngBounds[1], maxLat];
  }, [geoJSONFilterDisplayData]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !geoJSONFilterBounds) return;

    let [minLng, minLat, maxLng, maxLat] = geoJSONFilterBounds;
    const minDelta = 0.0001;

    // Avoid zero-width/height bounds, which can cause fitBounds to fail.
    if (Math.abs(maxLng - minLng) < minDelta) {
      minLng -= minDelta;
      maxLng += minDelta;
    }
    if (Math.abs(maxLat - minLat) < minDelta) {
      minLat -= minDelta;
      maxLat += minDelta;
    }

    try {
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        {
          padding: 50,
          duration: 700
        }
      );
    } catch (error) {
      // Ignore fit failures for malformed or empty bounds.
    }
  }, [geoJSONFilterBounds]);

  const updateBounds = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || !props.setMapBounds) return;
    const b = map.getBounds().toArray();
    if (!Array.isArray(b) || b.length !== 2) return;
    const [[minLng, minLat], [maxLng, maxLat]] = b;
    props.setMapBounds([minLng, minLat, maxLng, maxLat]);
  }, [props.setMapBounds]);

  return (
    <div className={props.className || 'postMap'}>
      <Map
        style={{ width: '100%', height: '100%' }}
        {...props.viewport}
        bearing={0}
        pitch={0}
        projection="globe"
        mapStyle="mapbox://styles/projectcivilian/cluf58pb1001x01r79z85fuh1"
        mapboxAccessToken={MAPBOX_TOKEN}
        interactiveLayerIds={[clusterLayer.id]}
        onLoad={() => {
          updateMaxClusterSize();
          updateBounds();
        }}
        onIdle={() => {
          updateMaxClusterSize();
          updateBounds();
        }}
        // onZoom={() => {
        //   updateMaxClusterSize();
        // }}
        onData={() => {
          // Required, otherwise the first click event won't fire.
          updateMaxClusterSize();
          
        }}
        onMove={(evt) => {
          const { viewState } = evt;
          const nextViewState = {
            ...viewState,
            bearing: 0,
            pitch: 0,
          };
          props.setViewport(nextViewState);
          updateBounds();
        }}
        onMoveEnd={(evt) => {
          const { viewState } = evt;
          if (!viewState) return;
          props.setCenterLongitude?.(viewState.longitude);
          props.setCenterLatitude?.(viewState.latitude);
          updateBounds();
        }}
        onMouseDown={(e) => {
          if (!props.isDrawingBox) return;
          setBoxStart(e.lngLat);
          setBoxCurrent(e.lngLat);
        }}
        onMouseMove={(e) => {
          if (!props.isDrawingBox || !boxStart) return;
          setBoxCurrent(e.lngLat);
        }}
        onMouseUp={(e) => {
          if (!props.isDrawingBox || !boxStart) return;
          const bounds = toBounds(boxStart, e.lngLat);
          props.setFocusBox?.(bounds);
          props.setIsDrawingBox?.(false);
          setBoxStart(null);
          setBoxCurrent(null);
        }}
        onTouchStart={(e) => {
          if (!props.isDrawingBox) return;
          const lngLat = getLngLatFromTouchEvent(e);
          if (!lngLat) return;
          e?.preventDefault?.();
          e?.originalEvent?.preventDefault?.();
          setBoxStart(lngLat);
          setBoxCurrent(lngLat);
        }}
        onTouchMove={(e) => {
          if (!props.isDrawingBox || !boxStart) return;
          const lngLat = getLngLatFromTouchEvent(e);
          if (!lngLat) return;
          e?.preventDefault?.();
          e?.originalEvent?.preventDefault?.();
          setBoxCurrent(lngLat);
        }}
        onTouchEnd={(e) => {
          if (!props.isDrawingBox || !boxStart) return;
          const lngLat = getLngLatFromTouchEvent(e) || boxCurrent || boxStart;
          const bounds = toBounds(boxStart, lngLat);
          props.setFocusBox?.(bounds);
          props.setIsDrawingBox?.(false);
          setBoxStart(null);
          setBoxCurrent(null);
        }}
        ref={mapRef}
        maxZoom={15}
        dragPan={!props.isDrawingBox}
        dragRotate={false}
        touchZoomRotate={{ zoom: true, rotate: false }}
        touchPitch={false}
        pitchWithRotate={false}
      > 
       {pointData && (
          <Source 
            id="projectCivilian" 
            type="geojson"
            data={{
              type: 'FeatureCollection',
              features: [pointData]
            }}>
            <Layer {...pointLayer} />
            <Layer {...pointLayerLabel} />
          </Source>
        )}
        <Source id="ab604" type="geojson" data="/AB-604.geojson">
          <Layer {...ab604FillLayer} />

          <Layer
            id="ab604-outline"
            type="line"
            minzoom={8}
            paint={{
              "line-color": "#6B7280",
              "line-width": 2
            }}
          />

          <Layer
            id="ab604-label"
            type="symbol"
            minzoom={8}
            layout={{
              "text-field": ["concat", "District ", ["get", "DISTRICT"]],
              "text-size": 14,
              "text-anchor": "center",
              "text-justify": "center",
              "symbol-placement": "point",
              "text-allow-overlap": true
            }}
            paint={{
              "text-color": "#111827",
              "text-halo-color": "#F9FAFB",
              "text-halo-width": 1.5
            }}
          />

        </Source>

        <Source
          id="postPoints"
          type="geojson"
          data={postPointData}
          cluster={true}  
          // clusterMaxZoom={14}
          clusterRadius={15}
        >
          <Layer {...clusterLayer(maxClusterSize)} />
          <Layer {...clusterCountLayer(maxClusterSize)} />
          <Layer {...unclusteredPointTwinkleLayer} />
          <Layer {...unclusteredPointLayer} />
        </Source>

       
        
        {props.currentLatitude && (
          <Source 
            id="current-location" 
            type="geojson"
            data={currentLocationData}>
            <Layer {...currentLocationLayer} />
            <Layer {...currentLocationLabel} 
            />
          </Source>
        )}

        {boxGeoJSON && (
          <Source id="focus-box" type="geojson" data={boxGeoJSON}>
            <Layer
              id="focus-box-fill"
              type="fill"
              paint={{
                "fill-color": "#830b0b",
                "fill-opacity": 0.12
              }}
            />
            <Layer
              id="focus-box-outline"
              type="line"
              paint={{
                "line-color": "#830b0b",
                "line-width": 1
              }}
            />
          </Source>
        )}

        {geoJSONFilterDisplayData && (
          <Source id="geojson-filter" type="geojson" data={geoJSONFilterDisplayData}>
            <Layer
              id="geojson-filter-fill"
              type="fill"
              paint={{
                "fill-color": "#830b0b",
                "fill-opacity": 0.12
              }}
            />
            <Layer
              id="geojson-filter-outline"
              type="line"
              paint={{
                "line-color": "#830b0b",
                "line-width": 1.5
              }}
            />
          </Source>
        )}
      </Map>
      {props.pointsLoading && (
        <div className="postMap__loading" aria-live="polite">
          <l-line-wobble
            size="120"
            stroke="5"
            bg-opacity="0.12"
            speed="1.5"
            color="#E2E8F0"
          ></l-line-wobble>
        </div>
      )}
    </div>
  );
};

export default PostMap;
